import React, { useMemo, useState, useEffect } from 'react';
import axios from 'axios';
import Select from 'react-select';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../AuthContext';
import {
    Card, CardBody, Button, Input, Chip
} from '@material-tailwind/react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faSpinner, faDownload, faEye, faFilter, faCar, faMoneyBillWave,
    faReceipt, faWallet, faChartBar, faTrash, faLock, faCheck,
    faCheckCircle, faExclamationTriangle, faRotateRight, faUsers
} from '@fortawesome/free-solid-svg-icons';
import { Alert } from '@material-tailwind/react';
import ConfirmModal from '../../common/ConfirmModal';
import { dateToApiWeek } from './remittanceWeekFilter';

const { rootUrl } = require('../../../config/config');

const RemittanceSummaryReport = () => {
    const { token, user } = useAuth();
    const navigate = useNavigate();
    const authToken = token || localStorage.getItem('authToken') || 'default-token';

    // State
    const [owners, setOwners] = useState([]);
    const [selectedOwner, setSelectedOwner] = useState(null);
    const [selectedStatus, setSelectedStatus] = useState(null);
    const [selectedWeek, setSelectedWeek] = useState(null);
    const [dateFrom, setDateFrom] = useState('');
    const [dateTo, setDateTo] = useState('');
    const [weekFrom, setWeekFrom] = useState('');
    const [weekTo, setWeekTo] = useState('');
    const [summaryData, setSummaryData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [filtered, setFiltered] = useState(false);
    const [deleteModalOpen, setDeleteModalOpen] = useState(false);
    const [remittanceToDelete, setRemittanceToDelete] = useState(null);
    const [deleteLoading, setDeleteLoading] = useState(false);
    const [confirmationText, setConfirmationText] = useState('');
    const [deleteProgress, setDeleteProgress] = useState({ current: 0, total: 0 });
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);
    const [selectedRows, setSelectedRows] = useState(new Set());
    const [bulkAction, setBulkAction] = useState(null);
    const [bulkLoading, setBulkLoading] = useState(false);
    const [bulkProgress, setBulkProgress] = useState({ current: 0, total: 0 });

    const statusOptions = [
        { value: 'PENDING', label: 'Pending' },
        { value: 'PROCESSED', label: 'Processed' },
        { value: 'APPROVED', label: 'Approved' },
        { value: 'PAID', label: 'Paid' }
    ];

    // Fetch initial data and load current year by default
    useEffect(() => {
        const initialize = async () => {
            await fetchOwners();
            // Set default to current year (2026)
            const currentYear = new Date().getFullYear();
            const defaultWeekFrom = `${currentYear}_01`;
            const defaultWeekTo = `${currentYear}_52`;
            setSelectedWeek(null);
            setDateFrom('');
            setDateTo('');
            setWeekFrom(defaultWeekFrom);
            setWeekTo(defaultWeekTo);
            // Auto-load current year data
            await loadData(null, null, defaultWeekFrom, defaultWeekTo);
        };
        initialize();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        setSelectedRows(new Set());
    }, [summaryData]);

    const fetchOwners = async () => {
        try {
            const response = await axios.get(`${rootUrl}/owners`, {
                headers: { Authorization: `Bearer ${authToken}` }
            });
            setOwners(response.data || []);
        } catch (error) {
            console.error('Error fetching owners:', error);
        }
    };

    const loadData = async (owner, status, wkFrom, wkTo) => {
        setLoading(true);
        try {
            const params = new URLSearchParams();
            if (owner) params.append('owner_code', owner.value);
            if (status) params.append('status', status.value);
            if (wkFrom) params.append('week_from', wkFrom);
            if (wkTo) params.append('week_to', wkTo);

            const response = await axios.get(
                `${rootUrl}/api/remittance/summary-historical?${params.toString()}`,
                { headers: { Authorization: `Bearer ${authToken}` } }
            );

            if (response.data.success) {
                setSummaryData(response.data.data || []);
                setFiltered(true);
            } else {
                setError(response.data.message || 'Failed to fetch remittance summary');
                setSummaryData([]);
            }
        } catch (error) {
            console.error('Error fetching remittance summary:', error);
            setError(error.response?.data?.message || 'Failed to fetch remittance summary');
            setSummaryData([]);
        } finally {
            setLoading(false);
        }
    };

    const handleApplyFilters = async () => {
        const effectiveWeekFrom = dateFrom ? dateToApiWeek(dateFrom) : weekFrom;
        const effectiveWeekTo = dateTo ? dateToApiWeek(dateTo) : weekTo;
        await loadData(selectedOwner, selectedStatus, effectiveWeekFrom, effectiveWeekTo);
    };

    const handleClearFilters = async () => {
        setSelectedOwner(null);
        setSelectedStatus(null);
        setSelectedWeek(null);
        setDateFrom('');
        setDateTo('');
        // Reset to current year
        const currentYear = new Date().getFullYear();
        const defaultWeekFrom = `${currentYear}_01`;
        const defaultWeekTo = `${currentYear}_52`;
        setWeekFrom(defaultWeekFrom);
        setWeekTo(defaultWeekTo);
        // Reload with current year data
        await loadData(null, null, defaultWeekFrom, defaultWeekTo);
    };

    const handleSelectedWeekChange = (option) => {
        setSelectedWeek(option);
        setDateFrom('');
        setDateTo('');
        setWeekFrom(option?.value || '');
        setWeekTo(option?.value || '');
    };

    const handleDrillDown = (row) => {
        // Navigate to owner remittance report with pre-filled filters
        navigate(`/remittance/owner-detail/${row.owner_code}/${row.week_key}`);
    };

    const handleDeleteClick = (row) => {
        setRemittanceToDelete(row);
        setConfirmationText('');
        setDeleteProgress({ current: 0, total: 0 });
        setDeleteModalOpen(true);
    };

    const handleConfirmDelete = async () => {
        if (!remittanceToDelete) return;

        // Validate confirmation text
        if (confirmationText.trim().toUpperCase() !== 'DELETE') {
            setError('Please type "DELETE" to confirm');
            return;
        }

        setDeleteLoading(true);
        try {
            // 1. Fetch individual remittance IDs for this owner/week
            const idsResponse = await axios.get(
                `${rootUrl}/api/remittance/ids-by-owner-week`,
                {
                    headers: { Authorization: `Bearer ${authToken}` },
                    params: {
                        owner_code: remittanceToDelete.owner_code,
                        week_key: remittanceToDelete.week_key
                    }
                }
            );

            if (!idsResponse.data.success || !idsResponse.data.data.length) {
                setError('No remittance records found to delete');
                setDeleteLoading(false);
                return;
            }

            const remittanceIds = idsResponse.data.data;
            const deletableIds = remittanceIds.filter(r => r.status === 'PENDING' || r.status === 'PROCESSED');

            if (deletableIds.length === 0) {
                setError('No deletable remittances found. Only PENDING or PROCESSED remittances can be deleted.');
                setDeleteLoading(false);
                return;
            }

            if (deletableIds.length < remittanceIds.length) {
                const cannotDelete = remittanceIds.length - deletableIds.length;
                setSuccess(`${cannotDelete} remittance(s) were skipped because only PENDING or PROCESSED rows can be deleted.`);
                setTimeout(() => setSuccess(null), 5000);
            }

            setDeleteProgress({ current: 0, total: deletableIds.length });

            // 2. Delete each remittance one by one
            let successCount = 0;
            let failCount = 0;
            const errors = [];

            for (let i = 0; i < deletableIds.length; i++) {
                const remittance = deletableIds[i];
                setDeleteProgress({ current: i + 1, total: deletableIds.length });

                try {
                    const deleteResponse = await axios.delete(
                        `${rootUrl}/api/remittance/${remittance.id}`,
                        {
                            headers: { Authorization: `Bearer ${authToken}` },
                            data: { reason: `Bulk delete for owner ${remittanceToDelete.owner_code} week ${remittanceToDelete.week_key}` }
                        }
                    );

                    if (deleteResponse.data.success) {
                        successCount++;
                    } else {
                        failCount++;
                        errors.push(`${remittance.plate_no}: ${deleteResponse.data.message}`);
                    }
                } catch (error) {
                    failCount++;
                    errors.push(`${remittance.plate_no}: ${error.response?.data?.message || error.message}`);
                }
            }

            // 3. Show results
            if (successCount > 0) {
                setSuccess(`Successfully deleted ${successCount} remittance(s).${failCount > 0 ? ` Failed: ${failCount}` : ''}`);
                setTimeout(() => setSuccess(null), 5000);
                setDeleteModalOpen(false);
                setRemittanceToDelete(null);
                setConfirmationText('');
                await loadData(selectedOwner, selectedStatus, weekFrom, weekTo); // Refresh data
            } else {
                setError(`Failed to delete remittances.\n${errors.join('\n')}`);
            }
        } catch (error) {
            console.error('Error deleting remittances:', error);
            setError('Error: ' + (error.response?.data?.message || error.message));
        } finally {
            setDeleteLoading(false);
            setDeleteProgress({ current: 0, total: 0 });
        }
    };

    const handleCancelDelete = () => {
        setDeleteModalOpen(false);
        setRemittanceToDelete(null);
        setConfirmationText('');
        setDeleteProgress({ current: 0, total: 0 });
    };

    const getRowKey = (row) => `${row.owner_code || ''}::${row.week_key || ''}::${row.status || ''}`;

    const selectedSummaryRows = useMemo(
        () => summaryData.filter((row) => selectedRows.has(getRowKey(row))),
        [summaryData, selectedRows]
    );

    const selectableRows = useMemo(
        () => summaryData.filter((row) => row.status === 'PENDING' || row.status === 'PROCESSED'),
        [summaryData]
    );

    const processEligibleRows = useMemo(
        () => selectedSummaryRows.filter((row) => row.status === 'PENDING'),
        [selectedSummaryRows]
    );

    const approveEligibleRows = useMemo(
        () => selectedSummaryRows.filter((row) => row.status === 'PROCESSED'),
        [selectedSummaryRows]
    );

    const toggleRowSelection = (row) => {
        if (row.status !== 'PENDING' && row.status !== 'PROCESSED') return;
        const rowKey = getRowKey(row);
        setSelectedRows((prev) => {
            const next = new Set(prev);
            if (next.has(rowKey)) {
                next.delete(rowKey);
            } else {
                next.add(rowKey);
            }
            return next;
        });
    };

    const toggleSelectAll = () => {
        const selectableKeys = selectableRows.map(getRowKey);
        const allSelected = selectableKeys.length > 0 && selectableKeys.every((key) => selectedRows.has(key));
        setSelectedRows(allSelected ? new Set() : new Set(selectableKeys));
    };

    const fetchRemittanceIdsForRows = async (rows, status) => {
        const allIds = [];
        for (const row of rows) {
            const idsResponse = await axios.get(
                `${rootUrl}/api/remittance/ids-by-owner-week`,
                {
                    headers: { Authorization: `Bearer ${authToken}` },
                    params: {
                        owner_code: row.owner_code,
                        week_key: row.week_key
                    }
                }
            );

            if (!idsResponse.data.success) {
                throw new Error(idsResponse.data.message || `Failed to load IDs for ${row.owner_code} ${row.week_key}`);
            }

            allIds.push(...(idsResponse.data.data || []).filter((item) => item.status === status).map((item) => item.id));
        }
        return allIds;
    };

    const openBulkProcess = () => {
        if (processEligibleRows.length === 0) {
            setError('Select at least one PENDING owner/week row to process.');
            return;
        }
        setBulkAction('process');
    };

    const openBulkApprove = () => {
        if (approveEligibleRows.length === 0) {
            setError('Select at least one PROCESSED owner/week row to approve.');
            return;
        }
        setBulkAction('approve');
    };

    const closeBulkModal = () => {
        if (bulkLoading) return;
        setBulkAction(null);
        setBulkProgress({ current: 0, total: 0 });
    };

    const handleConfirmBulkAction = async () => {
        const isProcess = bulkAction === 'process';
        const rows = isProcess ? processEligibleRows : approveEligibleRows;
        const targetStatus = isProcess ? 'PENDING' : 'PROCESSED';

        setBulkLoading(true);
        setBulkProgress({ current: 0, total: rows.length });

        try {
            const remittanceIds = await fetchRemittanceIdsForRows(rows, targetStatus);
            if (remittanceIds.length === 0) {
                throw new Error(`No ${targetStatus} remittance records were found for the selected rows.`);
            }

            setBulkProgress({ current: 0, total: remittanceIds.length });

            if (isProcess) {
                for (let i = 0; i < remittanceIds.length; i++) {
                    setBulkProgress({ current: i + 1, total: remittanceIds.length });
                    await axios.put(
                        `${rootUrl}/api/remittance/${remittanceIds[i]}/process`,
                        { processed_by: user?.username || 'system' },
                        { headers: { Authorization: `Bearer ${authToken}` } }
                    );
                }
            } else {
                setBulkProgress({ current: remittanceIds.length, total: remittanceIds.length });
                await axios.post(
                    `${rootUrl}/api/remittance/approve-bulk`,
                    { remittance_ids: remittanceIds, approved_by: user?.username || 'system' },
                    { headers: { Authorization: `Bearer ${authToken}` } }
                );
            }

            setSuccess(`${isProcess ? 'Processed' : 'Approved'} ${remittanceIds.length} remittance record(s) from ${rows.length} owner/week row(s).`);
            setTimeout(() => setSuccess(null), 5000);
            setBulkAction(null);
            setSelectedRows(new Set());
            await handleApplyFilters();
        } catch (error) {
            console.error('Bulk remittance action failed:', error);
            setError(error.response?.data?.message || error.message || 'Bulk action failed');
        } finally {
            setBulkLoading(false);
            setBulkProgress({ current: 0, total: 0 });
        }
    };

    const handleExportCSV = () => {
        if (summaryData.length === 0) {
            setError('No data to export');
            return;
        }

        const headers = ['Owner', 'Week', 'Status', 'Total Cars', 'Total Remittance', 'Total Charges', 'Owner Payout', 'Payment Date', 'Payment Reference'];
        const rows = summaryData.map(row => [
            row.owner_name || row.owner_code,
            row.week_key,
            row.status,
            row.total_cars,
            row.total_remittance?.toFixed(2) || '0.00',
            row.total_charges?.toFixed(2) || '0.00',
            row.owner_payout?.toFixed(2) || '0.00',
            row.payment_date ? new Date(row.payment_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '',
            row.payment_reference || ''
        ]);

        const csvContent = [
            headers.join(','),
            ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
        ].join('\n');

        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        const url = URL.createObjectURL(blob);
        link.setAttribute('href', url);
        link.setAttribute('download', `remittance-summary-${new Date().toISOString().split('T')[0]}.csv`);
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    const ownerOptions = owners.map(owner => ({
        value: owner.code,
        label: owner.name
    }));

    const weekOptions = useMemo(() => {
        const currentYear = new Date().getFullYear();
        return Array.from({ length: 52 }, (_, index) => {
            const weekNumber = String(index + 1).padStart(2, '0');
            const value = `${currentYear}_${weekNumber}`;
            return { value, label: `Week ${weekNumber} (${value})` };
        });
    }, []);

    const summaryMetrics = useMemo(() => {
        const statusBreakdown = summaryData.reduce((acc, row) => {
            acc[row.status] = (acc[row.status] || 0) + 1;
            return acc;
        }, {});

        return {
            totalRecords: summaryData.length,
            totalCars: summaryData.reduce((sum, row) => sum + parseInt(row.total_cars || 0), 0),
            totalRemittance: summaryData.reduce((sum, row) => sum + parseFloat(row.total_remittance || 0), 0),
            totalCharges: summaryData.reduce((sum, row) => sum + parseFloat(row.total_charges || 0), 0),
            totalPayout: summaryData.reduce((sum, row) => sum + parseFloat(row.owner_payout || 0), 0),
            statusBreakdown
        };
    }, [summaryData]);

    const allSelectableSelected = selectableRows.length > 0
        && selectableRows.every((row) => selectedRows.has(getRowKey(row)));

    return (
        <div className="min-h-screen bg-[#f6f7f9]">
            <div className="border-b border-gray-200 bg-white">
                <div className="w-full max-w-7xl mx-auto px-4 sm:px-6 py-5">
                    <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
                        <div>
                            <h1 className="text-xl font-bold text-[#2a2e30]">Remittance Summary</h1>
                            <p className="text-xs text-gray-500 mt-1">
                                Owner/week remittance batches for review, processing, approval, and export.
                            </p>
                        </div>
                        <div className="flex flex-wrap gap-2">
                            <Button
                                onClick={handleExportCSV}
                                disabled={summaryData.length === 0}
                                className="bg-white border border-gray-300 text-gray-700 font-semibold px-4 py-2 rounded-sm shadow-none disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                <FontAwesomeIcon icon={faDownload} className="mr-2" />
                                Export CSV
                            </Button>
                            <Button
                                onClick={handleApplyFilters}
                                disabled={loading}
                                className="bg-[#FF5733] hover:bg-[#e64520] text-white font-semibold px-4 py-2 rounded-sm shadow-none"
                            >
                                {loading ? (
                                    <>
                                        <FontAwesomeIcon icon={faSpinner} className="animate-spin mr-2" />
                                        Loading
                                    </>
                                ) : (
                                    <>
                                        <FontAwesomeIcon icon={faRotateRight} className="mr-2" />
                                        Refresh
                                    </>
                                )}
                            </Button>
                        </div>
                    </div>
                </div>
            </div>

            <div className="w-full max-w-7xl mx-auto px-4 sm:px-6 py-6">
                {/* Filters Card */}
                <Card className="mb-4 shadow-sm rounded-sm border border-gray-200">
                    <CardBody className="p-4">
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-4">
                            {/* Owner Filter */}
                            <div>
                                <label className="block text-xs font-semibold uppercase text-gray-500 mb-1">Owner</label>
                                <Select
                                    options={ownerOptions}
                                    value={selectedOwner}
                                    onChange={setSelectedOwner}
                                    isClearable
                                    placeholder="Select owner..."
                                />
                            </div>

                            {/* Status Filter */}
                            <div>
                                <label className="block text-xs font-semibold uppercase text-gray-500 mb-1">Status</label>
                                <Select
                                    options={statusOptions}
                                    value={selectedStatus}
                                    onChange={setSelectedStatus}
                                    isClearable
                                    placeholder="Select status..."
                                />
                            </div>

                            {/* Week Filter */}
                            <div>
                                <label className="block text-xs font-semibold uppercase text-gray-500 mb-1">Week</label>
                                <Select
                                    options={weekOptions}
                                    value={selectedWeek}
                                    onChange={handleSelectedWeekChange}
                                    isClearable
                                    isSearchable
                                    placeholder="Select week..."
                                />
                            </div>

                            {/* Date From */}
                            <div>
                                <label className="block text-xs font-semibold uppercase text-gray-500 mb-1">Date From</label>
                                <Input
                                    type="date"
                                    value={dateFrom}
                                    onChange={(e) => setDateFrom(e.target.value)}
                                    className="border-gray-300"
                                    crossOrigin="anonymous"
                                />
                            </div>

                            {/* Date To */}
                            <div>
                                <label className="block text-xs font-semibold uppercase text-gray-500 mb-1">Date To</label>
                                <Input
                                    type="date"
                                    value={dateTo}
                                    onChange={(e) => setDateTo(e.target.value)}
                                    className="border-gray-300"
                                    crossOrigin="anonymous"
                                />
                            </div>

                            <div className="flex items-end gap-2">
                                <Button
                                    onClick={handleApplyFilters}
                                    disabled={loading}
                                    className="bg-[#FF5733] hover:bg-[#e64520] text-white font-semibold px-4 py-2 rounded-sm shadow-none"
                                >
                                    <FontAwesomeIcon icon={faFilter} className="mr-2" />
                                    Apply
                                </Button>
                                <Button
                                    onClick={handleClearFilters}
                                    className="bg-white border border-gray-300 hover:bg-gray-50 text-gray-700 font-semibold px-4 py-2 rounded-sm shadow-none"
                                >
                                    Clear
                                </Button>
                            </div>
                        </div>
                    </CardBody>
                </Card>

                {/* Summary Cards */}
                {filtered && summaryData.length > 0 && (
                        <div className="grid grid-cols-2 lg:grid-cols-6 gap-3 mb-4">
                            {[
                                { label: 'Records', value: summaryMetrics.totalRecords, icon: faChartBar },
                                { label: 'Owners', value: new Set(summaryData.map((row) => row.owner_code)).size, icon: faUsers },
                                { label: 'Cars', value: summaryMetrics.totalCars, icon: faCar },
                                { label: 'Remittance', value: summaryMetrics.totalRemittance.toLocaleString('en-US', { maximumFractionDigits: 0 }), icon: faMoneyBillWave },
                                { label: 'Charges', value: summaryMetrics.totalCharges.toLocaleString('en-US', { maximumFractionDigits: 0 }), icon: faReceipt },
                                { label: 'Owner Payout', value: summaryMetrics.totalPayout.toLocaleString('en-US', { maximumFractionDigits: 0 }), icon: faWallet, strong: true }
                            ].map((metric) => (
                                <div key={metric.label} className={`border border-gray-200 bg-white rounded-sm p-3 ${metric.strong ? 'border-l-4 border-l-[#FF5733]' : ''}`}>
                                    <div className="flex items-start justify-between gap-2">
                                        <div>
                                            <div className="text-xs uppercase font-semibold text-gray-500">{metric.label}</div>
                                            <div className="mt-1 text-base font-bold text-[#2a2e30] break-words">{metric.value}</div>
                                        </div>
                                        <FontAwesomeIcon icon={metric.icon} className="text-gray-400 mt-1" />
                                    </div>
                                </div>
                            ))}
                            <div className="col-span-2 lg:col-span-6 flex flex-wrap gap-2 text-xs text-gray-600">
                                {Object.entries(summaryMetrics.statusBreakdown).map(([status, count]) => (
                                    <span key={status} className="inline-flex items-center gap-1 border border-gray-200 bg-white rounded-sm px-2 py-1">
                                        <span className="font-semibold">{status}</span>
                                        <span>{count}</span>
                                    </span>
                                ))}
                            </div>
                        </div>
                )}

                {/* Results */}
                <div className="mb-4">
                    {error && (
                        <Alert
                            color="red"
                            icon={<FontAwesomeIcon icon={faExclamationTriangle} />}
                            className="mb-4"
                            onClose={() => setError(null)}
                        >
                            <div className="whitespace-pre-wrap">{error}</div>
                        </Alert>
                    )}
                    {success && (
                        <Alert
                            color="green"
                            icon={<FontAwesomeIcon icon={faCheckCircle} />}
                            className="mb-4"
                            onClose={() => setSuccess(null)}
                        >
                            {success}
                        </Alert>
                    )}
                </div>

                {filtered && (
                    <Card className="shadow-sm rounded-sm overflow-hidden border border-gray-200">
                        <CardBody className="p-0">
                            {summaryData.length === 0 ? (
                                <div className="p-8 text-center text-gray-500">
                                    <p className="text-sm">No remittance records found for the selected filters</p>
                                </div>
                            ) : (
                                <>
                                    <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between px-4 py-3 bg-white border-b border-gray-200">
                                        <div>
                                            <div className="text-xs font-semibold text-[#2a2e30]">
                                                {selectedRows.size} selected
                                            </div>
                                            <div className="text-xs text-gray-500">
                                                {processEligibleRows.length} pending can be processed · {approveEligibleRows.length} processed can be approved
                                            </div>
                                        </div>
                                        <div className="flex flex-wrap gap-2">
                                            <Button
                                                size="sm"
                                                onClick={openBulkProcess}
                                                disabled={processEligibleRows.length === 0 || bulkLoading}
                                                className="bg-white border border-gray-300 text-gray-700 font-semibold rounded-sm shadow-none disabled:opacity-50"
                                            >
                                                <FontAwesomeIcon icon={faLock} className="mr-2" />
                                                Process selected
                                            </Button>
                                            <Button
                                                size="sm"
                                                onClick={openBulkApprove}
                                                disabled={approveEligibleRows.length === 0 || bulkLoading}
                                                className="bg-[#FF5733] hover:bg-[#e64520] text-white font-semibold rounded-sm shadow-none disabled:opacity-50"
                                            >
                                                <FontAwesomeIcon icon={faCheck} className="mr-2" />
                                                Approve selected
                                            </Button>
                                        </div>
                                    </div>
                                    <div className="overflow-x-auto">
                                        <table className="w-full text-xs">
                                            <thead>
                                                <tr className="bg-gray-50 border-b border-gray-200">
                                                    <th className="text-left px-3 py-3 font-semibold text-[#2a2e30] w-10">
                                                        <input
                                                            type="checkbox"
                                                            checked={allSelectableSelected}
                                                            onChange={toggleSelectAll}
                                                            disabled={selectableRows.length === 0}
                                                            aria-label="Select all processable or approvable rows"
                                                        />
                                                    </th>
                                                    <th className="text-left px-4 py-3 font-semibold text-[#2a2e30]">Owner</th>
                                                    <th className="text-center px-4 py-3 font-semibold text-[#2a2e30]">Week</th>
                                                    <th className="text-center px-4 py-3 font-semibold text-[#2a2e30]">Status</th>
                                                    <th className="text-right px-4 py-3 font-semibold text-[#2a2e30]">Cars</th>
                                                    <th className="text-right px-4 py-3 font-semibold text-[#2a2e30]">Remittance</th>
                                                    <th className="text-right px-4 py-3 font-semibold text-[#2a2e30]">Charges</th>
                                                    <th className="text-right px-4 py-3 font-semibold text-[#2a2e30]">Payout</th>
                                                    <th className="text-center px-4 py-3 font-semibold text-[#2a2e30]">Actions</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {summaryData.map((row) => {
                                                    const rowKey = getRowKey(row);
                                                    const selectable = row.status === 'PENDING' || row.status === 'PROCESSED';
                                                    return (
                                                        <tr key={rowKey} className={`border-b border-gray-100 hover:bg-gray-50 transition ${selectedRows.has(rowKey) ? 'bg-orange-50' : 'bg-white'}`}>
                                                            <td className="px-3 py-3">
                                                                <input
                                                                    type="checkbox"
                                                                    checked={selectedRows.has(rowKey)}
                                                                    disabled={!selectable}
                                                                    onChange={() => toggleRowSelection(row)}
                                                                    aria-label={`Select ${row.owner_code} ${row.week_key}`}
                                                                />
                                                            </td>
                                                            <td className="px-4 py-3 text-[#2a2e30] font-medium">
                                                                <Link
                                                                    to={`/owners/statements/${encodeURIComponent(row.owner_code || '')}`}
                                                                    className="inline-flex flex-col hover:text-[#FF5733] hover:underline"
                                                                    title="Open owner statement"
                                                                >
                                                                    <span>{row.owner_name || row.owner_code}</span>
                                                                    <span className="text-xs text-gray-500">{row.owner_code}</span>
                                                                </Link>
                                                            </td>
                                                            <td className="px-4 py-3 text-center text-[#2a2e30] whitespace-nowrap">
                                                                {row.week_key}
                                                            </td>
                                                            <td className="px-4 py-3 text-center">
                                                                <Chip
                                                                    value={row.status}
                                                                    className={`w-fit mx-auto rounded-sm ${row.status === 'PAID'
                                                                        ? 'bg-green-100 text-green-800'
                                                                        : row.status === 'APPROVED'
                                                                            ? 'bg-blue-100 text-blue-800'
                                                                            : row.status === 'PROCESSED'
                                                                                ? 'bg-yellow-100 text-yellow-800'
                                                                                : 'bg-gray-100 text-gray-800'
                                                                        }`}
                                                                />
                                                            </td>
                                                            <td className="px-4 py-3 text-right text-[#2a2e30]">
                                                                {row.total_cars}
                                                            </td>
                                                            <td className="px-4 py-3 text-right text-[#2a2e30] font-medium">
                                                                {parseFloat(row.total_remittance || 0).toLocaleString('en-US', {
                                                                    minimumFractionDigits: 2,
                                                                    maximumFractionDigits: 2
                                                                })}
                                                            </td>
                                                            <td className="px-4 py-3 text-right text-[#2a2e30] font-medium">
                                                                {parseFloat(row.total_charges || 0).toLocaleString('en-US', {
                                                                    minimumFractionDigits: 2,
                                                                    maximumFractionDigits: 2
                                                                })}
                                                            </td>
                                                            <td className={`px-4 py-3 text-right font-bold ${parseFloat(row.owner_payout || 0) < 0 ? 'text-red-700' : 'text-[#2a2e30]'}`}>
                                                                {parseFloat(row.owner_payout || 0).toLocaleString('en-US', {
                                                                    minimumFractionDigits: 2,
                                                                    maximumFractionDigits: 2
                                                                })}
                                                            </td>
                                                            <td className="px-4 py-3 text-center">
                                                                <div className="flex items-center justify-center gap-2">
                                                                    <button
                                                                        onClick={() => handleDrillDown(row)}
                                                                        className="inline-flex items-center justify-center p-2 rounded-sm bg-slate-100 hover:bg-slate-200 text-slate-700 transition"
                                                                        title="View detailed report"
                                                                    >
                                                                        <FontAwesomeIcon icon={faEye} />
                                                                    </button>
                                                                    {(row.status === 'PENDING' || row.status === 'PROCESSED') && (
                                                                        <button
                                                                            onClick={() => handleDeleteClick(row)}
                                                                            className="inline-flex items-center justify-center p-2 rounded-sm bg-red-100 hover:bg-red-200 text-red-700 transition"
                                                                            title="Delete remittance"
                                                                        >
                                                                            <FontAwesomeIcon icon={faTrash} />
                                                                        </button>
                                                                    )}
                                                                </div>
                                                            </td>
                                                        </tr>
                                                    );
                                                })}
                                            </tbody>
                                        </table>
                                    </div>
                                </>
                            )}
                            <div className="px-4 py-3 bg-gray-50 text-xs text-gray-600 flex justify-between">
                                <span>Total records: {summaryData.length}</span>
                                <span>Selectable: {selectableRows.length}</span>
                            </div>
                        </CardBody>
                    </Card>
                )}

                {!filtered && summaryData.length === 0 && (
                    <Card className="shadow-sm rounded-sm text-center p-8 border border-gray-200">
                        <p className="text-gray-500 text-sm">Loading remittance data...</p>
                    </Card>
                )}

                <ConfirmModal
                    isOpen={deleteModalOpen}
                    title="Delete owner/week remittances"
                    description={remittanceToDelete
                        ? `Archive deletable remittances for ${remittanceToDelete.owner_name || remittanceToDelete.owner_code}, week ${remittanceToDelete.week_key}. This can include up to ${remittanceToDelete.total_cars} car record(s); approved or paid records will be skipped. ${deleteLoading && deleteProgress.total > 0 ? `Deleting ${deleteProgress.current} of ${deleteProgress.total}.` : ''}`
                        : ''}
                    typedConfirmLabel='Type DELETE to confirm'
                    typedConfirmValue={confirmationText}
                    onTypedConfirmChange={setConfirmationText}
                    onCancel={handleCancelDelete}
                    onConfirm={handleConfirmDelete}
                    confirmText="Delete"
                    cancelText="Cancel"
                    loading={deleteLoading}
                    error={confirmationText && confirmationText.trim().toUpperCase() !== 'DELETE' ? 'Confirmation text must be DELETE.' : null}
                    variant="danger"
                />

                <ConfirmModal
                    isOpen={Boolean(bulkAction)}
                    title={bulkAction === 'process' ? 'Process selected remittances' : 'Approve selected remittances'}
                    description={bulkAction === 'process'
                        ? `Process ${processEligibleRows.length} selected owner/week row(s). This locks their PENDING remittance records for approval. ${bulkLoading && bulkProgress.total > 0 ? `Processed ${bulkProgress.current} of ${bulkProgress.total}.` : ''}`
                        : `Approve ${approveEligibleRows.length} selected owner/week row(s). This posts their PROCESSED remittance records to owner statements. ${bulkLoading && bulkProgress.total > 0 ? `Approved ${bulkProgress.current} of ${bulkProgress.total}.` : ''}`
                    }
                    onCancel={closeBulkModal}
                    onConfirm={handleConfirmBulkAction}
                    confirmText={bulkAction === 'process' ? 'Process' : 'Approve'}
                    cancelText="Cancel"
                    loading={bulkLoading}
                    variant="primary"
                />
            </div>
        </div>
    );
};

export default RemittanceSummaryReport;
